/**
 * Página «Categorías»: al tocar una categoría de la barra lateral se cargan sus
 * productos con la Section Rendering API (section categories-grid), sin recargar.
 */
import { fetchSection, prefersReducedMotion } from '@theme/global';

const GRID_SECTION = 'categories-grid';

class CategoryBrowser extends HTMLElement {
  connectedCallback() {
    this.results = this.querySelector('[data-category-results]');
    this.limit = parseInt(this.dataset.limit, 10) || 24;
    this.cache = new Map();
    this.requestId = 0;

    this.addEventListener('click', (event) => {
      const parent = event.target.closest('.categories__item[data-category-url]');
      if (parent) {
        event.preventDefault();
        this.selectParent(parent);
        return;
      }
      const sub = event.target.closest('[data-subcat]');
      if (sub) {
        event.preventDefault();
        this.selectSub(sub);
      }
    });

    const match = window.location.hash.match(/^#cat-(\d+)$/);
    if (match) {
      const item = this.querySelector(`.categories__item[data-category-index="${match[1]}"]`);
      if (item && !item.classList.contains('is-active')) this.selectParent(item, false);
    }
  }

  selectParent(item, updateHash = true) {
    this.querySelectorAll('.categories__item').forEach((element) => {
      const active = element === item;
      element.classList.toggle('is-active', active);
      if (active) element.setAttribute('aria-current', 'true');
      else element.removeAttribute('aria-current');
    });

    const index = item.dataset.categoryIndex;
    this.querySelectorAll('[data-subcats]').forEach((list) => {
      const visible = list.dataset.subcats === index;
      list.hidden = !visible;
      if (visible) {
        const chips = [...list.querySelectorAll('[data-subcat]')];
        const target = chips.find((chip) => chip.dataset.categoryUrl === item.dataset.categoryUrl) || chips[0];
        this.markChip(list, target);
      }
    });

    if (updateHash) window.history.replaceState(null, '', `#cat-${index}`);
    this.load(item.dataset.categoryUrl);
  }

  selectSub(chip) {
    this.markChip(chip.closest('[data-subcats]'), chip);
    this.load(chip.dataset.categoryUrl);
  }

  markChip(list, active) {
    list.querySelectorAll('.chip').forEach((chip) => {
      const isActive = chip === active;
      chip.classList.toggle('is-active', isActive);
      if (isActive) chip.setAttribute('aria-current', 'true');
      else chip.removeAttribute('aria-current');
    });
  }

  async load(url) {
    if (!url || !this.results) return;
    const requestId = ++this.requestId;
    this.results.classList.add('is-loading');
    this.results.setAttribute('aria-busy', 'true');

    try {
      let html = this.cache.get(url);
      if (!html) {
        const doc = await fetchSection(url, GRID_SECTION);
        const content = doc.querySelector('.categories-results');
        if (!content) throw new Error('empty');
        [...content.querySelectorAll('[data-grid-item]')].slice(this.limit).forEach((item) => item.remove());
        html = content.outerHTML;
        this.cache.set(url, html);
      }
      if (requestId !== this.requestId) return;
      this.results.innerHTML = html;

      const top = this.getBoundingClientRect().top;
      if (top < 0) {
        window.scrollTo({ top: window.scrollY + top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    } catch (err) {
      window.location.href = url;
    } finally {
      if (requestId === this.requestId) {
        this.results.classList.remove('is-loading');
        this.results.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('category-browser')) {
  customElements.define('category-browser', CategoryBrowser);
}
